import { getDateString } from "@redotech/util/date";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  DynamicCustomerGroup,
  StaticCustomerGroup,
} from "../../customers/customer-group-definition";
import { ConjunctionMode } from "../../segments/segment-types";
import {
  ComparisonType,
  NumericCompareOperator,
  AnnualDateCondition as OriginalAnnualDateCondition,
  BooleanCondition as OriginalBooleanCondition,
  CityProximityCondition as OriginalCityProximityCondition,
  CustomFieldCondition as OriginalCustomFieldCondition,
  FullDateCondition as OriginalFullDateCondition,
  NumericCondition as OriginalNumericCondition,
  StringCondition as OriginalStringCondition,
  TokenCondition as OriginalTokenCondition,
  TokenHierarchyCondition as OriginalTokenHierarchyCondition,
  TokenListCondition as OriginalTokenListCondition,
  WhereCondition as OriginalWhereCondition,
  WhereConditionDataType,
} from "../../segments/segment-where-condition";
import {
  AnnualDateCondition,
  BooleanCondition,
  CityProximityCondition,
  CustomFieldCondition,
  FullDateCondition,
  NumericCondition,
  TokenCondition,
  TokenHierarchyCondition,
  TokenListCondition,
  whereConditionSchema,
  whereConditionSchemaForDimensions,
} from "../../segments/segment-where-condition-zod-schema";
import { refineSegmentCondition } from "./refine-segment-condition";
import {
  CUSTOMER_CHARACTERISTIC_DATA_STRUCTURE,
  segmentDataStructureTypeToDataStructure,
} from "./segment-data-structures";
import {
  AnnualTimeframeCondition,
  FullDateTimeframeCondition,
  TimeframeConditionType,
} from "./segment-timeframe";
import {
  annualDateTimeframeSchema,
  fullDateTimeframeSchema,
} from "./segment-timeframe-zod-schema";
import {
  ActivityCount,
  ActivityCountType,
  CustomerActivityCondition,
  CustomerActivityType,
  CustomerCharacteristicType,
  CustomEventCondition as OriginalCustomEventCondition,
  CustomerAttributeCondition as OriginalCustomerAttributeCondition,
  Segment,
  SegmentCondition,
  SegmentConditionBlock,
  SegmentConditionType,
} from "./segment-types";

// Common schemas
// const aggregationTypeSchema = z.enum(["count", "sum", "avg", "min", "max"]);
const aggregationTypeSchema = z.literal("count");

// Value schemas
const constantValueSchema = z.object({
  type: z.literal("constant"),
  value: z.union([z.string(), z.number()]),
});

const aggregationValueSchema = z.object({
  type: z.literal("aggregation"),
  aggregation: aggregationTypeSchema,
  options: z.null(),
});

const eventOperatorSchema = createEnum(NumericCompareOperator, {
  enumName: "NumericCompareOperator",
});

// Condition schemas
const baseConditionSchema = z.object({
  type: createEnum(SegmentConditionType, { enumName: "SegmentConditionType" }),
});

// Individual condition schemas
/** A data structure's dimensions, split by the comparison kind each supports. */
function dimensionsByDataType(
  dataStructure: keyof typeof segmentDataStructureTypeToDataStructure,
): Partial<Record<WhereConditionDataType, [string, ...string[]]>> {
  const grouped: Partial<
    Record<WhereConditionDataType, [string, ...string[]]>
  > = {};
  for (const [name, field] of Object.entries(
    segmentDataStructureTypeToDataStructure[dataStructure].fields,
  )) {
    const existing = grouped[field.dataType];
    if (existing) {
      existing.push(name);
    } else {
      grouped[field.dataType] = [name];
    }
  }
  return grouped;
}

const customerAttributeConditionSchema = baseConditionSchema.extend({
  type: z.literal(SegmentConditionType.CUSTOMER_ATTRIBUTE),
  whereCondition: whereConditionSchemaForDimensions(
    dimensionsByDataType(CUSTOMER_CHARACTERISTIC_DATA_STRUCTURE),
  ),
});

const customerEventCountSchema = z.object({
  operator: eventOperatorSchema,
  value: z.number(),
});

const customerEventConditionSchema = baseConditionSchema.extend({
  type: z.literal(SegmentConditionType.CUSTOMER_ACTIVITY),
  event: createEnum(CustomerActivityType, { enumName: "CustomerActivityType" }),
  count: customerEventCountSchema,
  timeframe: fullDateTimeframeSchema,
  event_filters: z.array(whereConditionSchema),
});

const customEventConditionSchema = baseConditionSchema.extend({
  type: z.literal(SegmentConditionType.CUSTOM_EVENT),
  eventName: z.string(),
  count: customerEventCountSchema,
  timeframe: fullDateTimeframeSchema,
  property_filters: z.array(whereConditionSchema),
});

// Union of all condition types
const queryConditionUnion = z.union([
  customerAttributeConditionSchema,
  customerEventConditionSchema,
  customEventConditionSchema,
]);
const queryConditionSchema_ = queryConditionUnion.superRefine(
  refineSegmentCondition,
);
export const queryConditionSchema: z.ZodType<QueryCondition> =
  queryConditionSchema_;

export const conditionBlockSchema = z.object({
  operator: createEnum(ConjunctionMode, { enumName: "ConjunctionMode" }),
  conditions: z.array(queryConditionSchema_),
});
export interface ConditionBlock extends z.infer<typeof conditionBlockSchema> {}

const segmentSchema_ = z.object({
  conjunction: createEnum(ConjunctionMode, { enumName: "ConjunctionMode" }),
  conditionBlocks: z.array(conditionBlockSchema),
});
export const segmentSchema: z.ZodType<SegmentQuery> = segmentSchema_;

// Export type definitions
export type EventOperator = z.infer<typeof eventOperatorSchema>;
export type AggregationType = z.infer<typeof aggregationTypeSchema>;
export type ConstantValue = z.infer<typeof constantValueSchema>;
export type AggregationValue = z.infer<typeof aggregationValueSchema>;
export type FullDateTimeframe = z.infer<typeof fullDateTimeframeSchema>;
export type AnnualDateTimeframe = z.infer<typeof annualDateTimeframeSchema>;
export interface CustomerAttributeCondition extends z.infer<
  typeof customerAttributeConditionSchema
> {}
export interface CustomerEventCondition extends z.infer<
  typeof customerEventConditionSchema
> {}
export interface CustomEventConditionZod extends z.infer<
  typeof customEventConditionSchema
> {}
export type CustomerEventCount = z.infer<typeof customerEventCountSchema>;
export type QueryCondition = z.infer<typeof queryConditionSchema_>;
export interface SegmentQuery extends z.infer<typeof segmentSchema_> {}

export function segmentToZod(segment: Segment): SegmentQuery {
  return {
    conjunction: segment.mode,
    conditionBlocks: segment.conditionBlocks.map(conditionBlockToZod),
  };
}

export function segmentFromZod(
  segment: DynamicCustomerGroup | StaticCustomerGroup,
): Segment {
  const baseSegment = {
    name: segment.name,
    type: segment.type,
    lastUpdated: segment.updatedAt,
    initialStatistics: {
      eligibleCustomers: segment.count,
      emailSubscriberCount: segment.emailSubscriberCount,
      emailEligibleCount: segment.emailEligibleCount,
      smsSubscriberCount: segment.smsSubscriberCount,
    },
  };

  if (segment.type === "static") {
    return { ...baseSegment, mode: ConjunctionMode.AND, conditionBlocks: [] };
  } else {
    return {
      ...baseSegment,
      mode: segment.conditions.conjunction,
      conditionBlocks: segment.conditions.conditionBlocks.map(
        conditionBlockFromZod,
      ),
    };
  }
}

export function conditionBlockToZod(
  conditionBlock: SegmentConditionBlock,
): ConditionBlock {
  return {
    operator: conditionBlock.mode,
    conditions: conditionBlock.conditions.map(segmentConditionToZod),
  };
}

export function conditionBlockFromZod(
  conditionBlock: ConditionBlock,
): SegmentConditionBlock {
  return {
    mode: conditionBlock.operator,
    conditions: conditionBlock.conditions.map(segmentConditionFromZod),
  };
}

function segmentConditionToZod(
  segmentCondition: SegmentCondition,
): QueryCondition {
  switch (segmentCondition.type) {
    case SegmentConditionType.CUSTOMER_ATTRIBUTE:
      return customerAttributeConditionToZod(segmentCondition);
    case SegmentConditionType.CUSTOMER_ACTIVITY:
      return customerActivityConditionToZod(segmentCondition);
    case SegmentConditionType.CUSTOM_EVENT:
      return customEventConditionToZod(segmentCondition);
    default:
      assertNever(segmentCondition);
  }
}

function segmentConditionFromZod(condition: QueryCondition): SegmentCondition {
  switch (condition.type) {
    case SegmentConditionType.CUSTOMER_ATTRIBUTE:
      return customerAttributeConditionFromZod(condition);
    case SegmentConditionType.CUSTOMER_ACTIVITY:
      return customerActivityConditionFromZod(condition);
    case SegmentConditionType.CUSTOM_EVENT:
      return customEventConditionFromZod(condition);
    default:
      assertNever(condition);
  }
}

function customerAttributeConditionToZod(
  condition: OriginalCustomerAttributeCondition,
): QueryCondition {
  return {
    type: SegmentConditionType.CUSTOMER_ATTRIBUTE,
    whereCondition: whereConditionToZod(condition.whereCondition),
  };
}

function customerActivityConditionToZod(
  condition: CustomerActivityCondition,
): QueryCondition {
  return {
    type: SegmentConditionType.CUSTOMER_ACTIVITY,
    event: condition.activityType,
    count: activityCountToZod(condition.count),
    timeframe: fullDateTimeframeToZod(condition.timeframe),
    event_filters: condition.whereConditions.map(whereConditionToZod),
  };
}

function customerAttributeConditionFromZod(
  condition: CustomerAttributeCondition,
): OriginalCustomerAttributeCondition {
  return {
    type: SegmentConditionType.CUSTOMER_ATTRIBUTE,
    whereCondition: whereConditionFromZod(
      condition.whereCondition,
    ) as OriginalWhereCondition<CustomerCharacteristicType>,
  };
}

function customerActivityConditionFromZod(
  condition: CustomerEventCondition,
): CustomerActivityCondition {
  return {
    type: SegmentConditionType.CUSTOMER_ACTIVITY,
    activityType: condition.event,
    count: activityCountFromZod(condition.count),
    timeframe: fullDateTimeframeFromZod(condition.timeframe),
    whereConditions: condition.event_filters.map(
      whereConditionFromZod,
    ) as OriginalWhereCondition<CustomerActivityType>[],
  };
}

function customEventConditionToZod(
  condition: OriginalCustomEventCondition,
): QueryCondition {
  return {
    type: SegmentConditionType.CUSTOM_EVENT,
    eventName: condition.eventName,
    count: activityCountToZod(condition.count),
    timeframe: fullDateTimeframeToZod(condition.timeframe),
    property_filters: condition.propertyFilters.map(whereConditionToZod),
  } as QueryCondition;
}

function customEventConditionFromZod(
  condition: CustomEventConditionZod,
): OriginalCustomEventCondition {
  return {
    type: SegmentConditionType.CUSTOM_EVENT,
    eventName: condition.eventName,
    count: activityCountFromZod(condition.count),
    timeframe: fullDateTimeframeFromZod(condition.timeframe),
    propertyFilters: condition.property_filters.map(
      whereConditionFromZod,
    ) as OriginalWhereCondition<string>[],
  };
}

function whereConditionToZod<D extends string>(
  condition: OriginalWhereCondition<D>,
) {
  switch (condition.type) {
    case WhereConditionDataType.DATE_FULL:
      return {
        ...condition,
        comparison: fullDateTimeframeToZod(condition.comparison),
      };
    case WhereConditionDataType.DATE_ANNUAL:
      return {
        ...condition,
        comparison: annualTimeframeToZod(condition.comparison),
      };
    case WhereConditionDataType.CUSTOM_FIELD:
      return customFieldWhereConditionToZod(condition);
    default:
      return condition;
  }
}

function whereConditionFromZod(
  condition: z.infer<typeof whereConditionSchema>,
): OriginalWhereCondition<string> {
  switch (condition.type) {
    case WhereConditionDataType.TOKEN:
      return tokenWhereConditionFromZod(condition);
    case WhereConditionDataType.NUMERIC:
      return numericWhereConditionFromZod(condition);
    case WhereConditionDataType.BOOLEAN:
      return booleanWhereConditionFromZod(condition);
    case WhereConditionDataType.DATE_FULL:
      return fullDateWhereConditionFromZod(condition);
    case WhereConditionDataType.DATE_ANNUAL:
      return annualDateWhereConditionFromZod(condition);
    case WhereConditionDataType.TOKEN_LIST:
      return tokenListWhereConditionFromZod(condition);
    case WhereConditionDataType.TOKEN_HIERARCHY:
      return tokenHierarchyWhereConditionFromZod(condition);
    case WhereConditionDataType.CITY_PROXIMITY:
      return cityProximityWhereConditionFromZod(condition);
    case WhereConditionDataType.CUSTOM_FIELD:
      return customFieldWhereConditionFromZod(condition);
    case WhereConditionDataType.STRING:
      return condition as OriginalStringCondition<string>;
    // case WhereConditionDataType.POSTAL_PROXIMITY:
    //   return postalProximityWhereConditionFromZod(condition);
    default:
      assertNever(condition);
  }
}

function tokenWhereConditionFromZod(
  condition: TokenCondition,
): OriginalTokenCondition<string> {
  return condition;
}

function numericWhereConditionFromZod(
  condition: NumericCondition,
): OriginalNumericCondition<string> {
  return condition;
}

function booleanWhereConditionFromZod(
  condition: BooleanCondition,
): OriginalBooleanCondition<string> {
  return condition;
}

function tokenListWhereConditionFromZod(
  condition: TokenListCondition,
): OriginalTokenListCondition<string> {
  return condition as OriginalTokenListCondition<string>;
}

function tokenHierarchyWhereConditionFromZod(
  condition: TokenHierarchyCondition,
): OriginalTokenHierarchyCondition<string> {
  return condition;
}

function fullDateWhereConditionFromZod(
  condition: FullDateCondition,
): OriginalFullDateCondition<string> {
  return {
    type: WhereConditionDataType.DATE_FULL,
    dimension: condition.dimension,
    comparison: fullDateTimeframeFromZod(condition.comparison),
  };
}

function annualDateWhereConditionFromZod(
  condition: AnnualDateCondition,
): OriginalAnnualDateCondition<string> {
  return {
    type: WhereConditionDataType.DATE_ANNUAL,
    dimension: condition.dimension,
    comparison: annualTimeframeFromZod(
      condition.comparison as z.infer<typeof annualDateTimeframeSchema>,
    ),
  };
}

function fullDateTimeframeToZod(
  condition: FullDateTimeframeCondition,
): z.infer<typeof fullDateTimeframeSchema> {
  switch (condition.type) {
    case TimeframeConditionType.BEFORE_NOW_RELATIVE:
      return {
        type: TimeframeConditionType.BEFORE_NOW_RELATIVE,
        options: { value: condition.value, units: condition.units },
      };
    case TimeframeConditionType.ON:
      return {
        type: TimeframeConditionType.ON,
        // condition.date is a string in inline segments in flows.
        // Would love to have inline segments fully typed,
        // but they aren't for now.
        options: { date: getDateString(new Date(condition.date)) },
      };
    case TimeframeConditionType.BEFORE_RELATIVE:
      return {
        type: TimeframeConditionType.BEFORE_RELATIVE,
        options: { value: condition.value, units: condition.units },
      };
    case TimeframeConditionType.BEFORE:
      return {
        type: TimeframeConditionType.BEFORE,
        options: { date: getDateString(new Date(condition.date)) },
      };
    case TimeframeConditionType.AFTER:
      return {
        type: TimeframeConditionType.AFTER,
        options: { date: getDateString(new Date(condition.date)) },
      };
    case TimeframeConditionType.ALL_TIME:
      return { type: TimeframeConditionType.ALL_TIME, options: null };
    case TimeframeConditionType.TODAY:
      return { type: TimeframeConditionType.TODAY, options: null };
    case TimeframeConditionType.BETWEEN_RELATIVE:
      return {
        type: TimeframeConditionType.BETWEEN_RELATIVE,
        options: {
          start: condition.start,
          end: condition.end,
          units: condition.units,
        },
      };
    case TimeframeConditionType.BETWEEN_DATES:
      return {
        type: TimeframeConditionType.BETWEEN_DATES,
        options: {
          range: condition.range.map((date) =>
            getDateString(new Date(date)),
          ) as [string, string],
        },
      };
    case TimeframeConditionType.AUTOMATION_START:
      return { type: TimeframeConditionType.AUTOMATION_START, options: null };
    default:
      assertNever(condition);
  }
}

function fullDateTimeframeFromZod(
  condition: z.infer<typeof fullDateTimeframeSchema>,
): FullDateTimeframeCondition {
  switch (condition.type) {
    case TimeframeConditionType.BEFORE_NOW_RELATIVE:
      return {
        type: TimeframeConditionType.BEFORE_NOW_RELATIVE,
        value: condition.options.value,
        units: condition.options.units,
      };
    case TimeframeConditionType.ON:
      return {
        type: TimeframeConditionType.ON,
        date: new Date(condition.options.date),
      };
    case TimeframeConditionType.BEFORE_RELATIVE:
      return {
        type: TimeframeConditionType.BEFORE_RELATIVE,
        value: condition.options.value,
        units: condition.options.units,
      };
    case TimeframeConditionType.BEFORE:
      return {
        type: TimeframeConditionType.BEFORE,
        date: new Date(condition.options.date),
      };
    case TimeframeConditionType.AFTER:
      return {
        type: TimeframeConditionType.AFTER,
        date: new Date(condition.options.date),
      };
    case TimeframeConditionType.ALL_TIME:
      return { type: TimeframeConditionType.ALL_TIME };
    case TimeframeConditionType.TODAY:
      return { type: TimeframeConditionType.TODAY };
    case TimeframeConditionType.BETWEEN_RELATIVE:
      return {
        type: TimeframeConditionType.BETWEEN_RELATIVE,
        start: condition.options.start,
        end: condition.options.end,
        units: condition.options.units,
      };
    case TimeframeConditionType.BETWEEN_DATES:
      return {
        type: TimeframeConditionType.BETWEEN_DATES,
        range: condition.options.range.map((date) => new Date(date)) as [
          Date,
          Date,
        ],
      };
    case TimeframeConditionType.AUTOMATION_START:
      return { type: TimeframeConditionType.AUTOMATION_START };
    default:
      assertNever(condition);
  }
}

function annualTimeframeToZod(
  condition: AnnualTimeframeCondition,
): z.infer<typeof annualDateTimeframeSchema> {
  switch (condition.type) {
    case TimeframeConditionType.BEFORE_NOW_RELATIVE:
      return {
        type: TimeframeConditionType.BEFORE_NOW_RELATIVE,
        options: { value: condition.value, units: condition.units },
      };
    case TimeframeConditionType.ON:
      return {
        type: TimeframeConditionType.ON,
        // condition.date is a string in inline segments in flows.
        // Would love to have inline segments fully typed,
        // but they aren't for now.
        options: { date: getDateString(new Date(condition.date)) },
      };
    case TimeframeConditionType.BEFORE:
      return {
        type: TimeframeConditionType.BEFORE,
        options: { date: getDateString(new Date(condition.date)) },
      };
    case TimeframeConditionType.AFTER:
      return {
        type: TimeframeConditionType.AFTER,
        options: { date: getDateString(new Date(condition.date)) },
      };
    case TimeframeConditionType.BEFORE_NOW_RELATIVE_EXACT:
      return {
        type: TimeframeConditionType.BEFORE_NOW_RELATIVE_EXACT,
        options: { value: condition.value, units: condition.units },
      };
    case TimeframeConditionType.AFTER_NOW_RELATIVE_EXACT:
      return {
        type: TimeframeConditionType.AFTER_NOW_RELATIVE_EXACT,
        options: { value: condition.value, units: condition.units },
      };
    case TimeframeConditionType.TODAY:
      return { type: TimeframeConditionType.TODAY, options: null };
    case TimeframeConditionType.BETWEEN_DATES:
      return {
        type: TimeframeConditionType.BETWEEN_DATES,
        options: {
          range: condition.range.map((date) =>
            getDateString(new Date(date)),
          ) as [string, string],
        },
      };
    case TimeframeConditionType.THIS_WEEK:
      return { type: TimeframeConditionType.THIS_WEEK, options: null };
    case TimeframeConditionType.THIS_MONTH:
      return { type: TimeframeConditionType.THIS_MONTH, options: null };
    default:
      assertNever(condition);
  }
}

function annualTimeframeFromZod(
  condition: z.infer<typeof annualDateTimeframeSchema>,
): AnnualTimeframeCondition {
  switch (condition.type) {
    case TimeframeConditionType.BEFORE_NOW_RELATIVE:
      return {
        type: TimeframeConditionType.BEFORE_NOW_RELATIVE,
        value: condition.options.value,
        units: condition.options.units,
      };
    case TimeframeConditionType.ON:
      return {
        type: TimeframeConditionType.ON,
        date: new Date(condition.options.date),
      };
    case TimeframeConditionType.BEFORE:
      return {
        type: TimeframeConditionType.BEFORE,
        date: new Date(condition.options.date),
      };
    case TimeframeConditionType.AFTER:
      return {
        type: TimeframeConditionType.AFTER,
        date: new Date(condition.options.date),
      };
    case TimeframeConditionType.BEFORE_NOW_RELATIVE_EXACT:
      return {
        type: TimeframeConditionType.BEFORE_NOW_RELATIVE_EXACT,
        value: condition.options.value,
        units: condition.options.units,
      };
    case TimeframeConditionType.AFTER_NOW_RELATIVE_EXACT:
      return {
        type: TimeframeConditionType.AFTER_NOW_RELATIVE_EXACT,
        value: condition.options.value,
        units: condition.options.units,
      };
    case TimeframeConditionType.TODAY:
      return { type: TimeframeConditionType.TODAY };
    case TimeframeConditionType.THIS_WEEK:
      return { type: TimeframeConditionType.THIS_WEEK };
    case TimeframeConditionType.THIS_MONTH:
      return { type: TimeframeConditionType.THIS_MONTH };
    case TimeframeConditionType.BETWEEN_DATES:
      return {
        type: TimeframeConditionType.BETWEEN_DATES,
        range: condition.options.range.map((date) => new Date(date)) as [
          Date,
          Date,
        ],
      };
    default:
      assertNever(condition);
  }
}

function activityCountToZod(
  count: ActivityCount,
): z.infer<typeof customerEventCountSchema> {
  switch (count.type) {
    case ActivityCountType.AT_LEAST_ONCE:
      return { operator: NumericCompareOperator.GT, value: 0 };
    case ActivityCountType.ZERO_TIMES:
      return { operator: NumericCompareOperator.EQ, value: 0 };
    case ActivityCountType.N_TIMES:
      return { operator: NumericCompareOperator.EQ, value: count.n };
    case ActivityCountType.NOT_N_TIMES:
      return { operator: NumericCompareOperator.NEQ, value: count.n };
    case ActivityCountType.AT_LEAST_N:
      return { operator: NumericCompareOperator.GTE, value: count.n };
    case ActivityCountType.GREATER_THAN_N:
      return { operator: NumericCompareOperator.GT, value: count.n };
    case ActivityCountType.LESS_THAN_N:
      return { operator: NumericCompareOperator.LT, value: count.n };
    case ActivityCountType.AT_MOST_N:
      return { operator: NumericCompareOperator.LTE, value: count.n };
    default:
      assertNever(count);
  }
}

function activityCountFromZod(
  count: z.infer<typeof customerEventCountSchema>,
): ActivityCount {
  switch (count.operator) {
    case NumericCompareOperator.GT:
      return count.value === 0
        ? { type: ActivityCountType.AT_LEAST_ONCE }
        : { type: ActivityCountType.GREATER_THAN_N, n: count.value };
    case NumericCompareOperator.EQ:
      return count.value === 0
        ? { type: ActivityCountType.ZERO_TIMES }
        : { type: ActivityCountType.N_TIMES, n: count.value };
    case NumericCompareOperator.NEQ:
      return { type: ActivityCountType.NOT_N_TIMES, n: count.value };
    case NumericCompareOperator.GTE:
      return { type: ActivityCountType.AT_LEAST_N, n: count.value };
    case NumericCompareOperator.LT:
      return { type: ActivityCountType.LESS_THAN_N, n: count.value };
    case NumericCompareOperator.LTE:
      return { type: ActivityCountType.AT_MOST_N, n: count.value };
    default:
      assertNever(count.operator);
  }
}

function customFieldWhereConditionToZod(
  condition: OriginalCustomFieldCondition<string>,
): CustomFieldCondition {
  const { comparison } = condition;
  switch (comparison.type) {
    case ComparisonType.TOKEN:
    case ComparisonType.NUMERIC:
    case ComparisonType.BOOLEAN:
    case ComparisonType.EXISTS:
      return condition as CustomFieldCondition;
    // Date custom fields use timeframe conditions that need format conversion
    default:
      return {
        ...condition,
        comparison: fullDateTimeframeToZod(comparison),
      } as CustomFieldCondition;
  }
}

function customFieldWhereConditionFromZod(
  condition: CustomFieldCondition,
): OriginalCustomFieldCondition<string> {
  const { comparison } = condition;
  switch (comparison.type) {
    case ComparisonType.TOKEN:
    case ComparisonType.NUMERIC:
    case ComparisonType.BOOLEAN:
    case ComparisonType.EXISTS:
      return condition as OriginalCustomFieldCondition<string>;
    // Date custom fields use timeframe conditions that need format conversion
    default:
      return {
        ...condition,
        comparison: fullDateTimeframeFromZod(
          comparison as z.infer<typeof fullDateTimeframeSchema>,
        ),
      } as OriginalCustomFieldCondition<string>;
  }
}

// function postalProximityWhereConditionToZod(
//   condition: OriginalPostalProximityCondition<string>,
// ): PostalProximityCondition {
//   return condition as PostalProximityCondition;
// }

function cityProximityWhereConditionFromZod(
  condition: CityProximityCondition,
): OriginalCityProximityCondition<string> {
  return condition as OriginalCityProximityCondition<string>;
}

// function postalProximityWhereConditionFromZod(
//   condition: PostalProximityCondition,
// ): OriginalPostalProximityCondition<string> {
//   return condition as OriginalPostalProximityCondition<string>;
// }
